import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import { useFmt } from '@/lib/useFmt.js';
import {
  Chip,
  EmptyState,
  GlassButton,
  LoadingState,
  PageHeader,
  Pagination,
  useConfirm,
} from '@/components/ui/index.js';
import type { AdminDeviceItem, AdminDevicesResponse } from '@/lib/types.js';

const PAGE_SIZE = 100;

type TypeFilter = 'all' | 'desktop' | 'mobile';
type PlatformFilter = 'all' | 'macos' | 'windows' | 'ios' | 'android';

const TYPE_LABEL: Record<'desktop' | 'mobile', string> = {
  desktop: '桌面',
  mobile: '移动',
};

const PLATFORM_LABEL: Record<string, string> = {
  macos: 'macOS',
  windows: 'Windows',
  ios: 'iOS',
  android: 'Android',
};

export function DevicesPage(): JSX.Element {
  const [data, setData] = useState<AdminDevicesResponse | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { fmtDateTime } = useFmt();
  const confirm = useConfirm();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (typeFilter !== 'all') params.set('device_type', typeFilter);
      if (platformFilter !== 'all') params.set('platform', platformFilter);
      if (includeRevoked) params.set('include_revoked', '1');
      const res = await request<AdminDevicesResponse>(`/admin/devices?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [offset, typeFilter, platformFilter, includeRevoked]);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(d: AdminDeviceItem): Promise<void> {
    const who = d.user_email ?? d.user_phone ?? d.user_id.slice(0, 8);
    const ok = await confirm({
      title: '强制下线该设备？',
      message: (
        <>
          用户 <strong>{who}</strong> 在 {TYPE_LABEL[d.device_type]} ({PLATFORM_LABEL[d.platform]})
          设备的会话会立即失效。下次他用同一设备打开时会被踢回登录页。
        </>
      ),
      tone: 'danger',
      confirmLabel: '确认下线',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await request(`/admin/devices/${d.id}/revoke`, { method: 'POST' });
      useToast.getState().show('success', '已强制下线');
      await load();
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof ApiError ? err.message : '操作失败',
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="设备"
        subtitle="全部用户的设备绑定记录。每用户每类型最多 1 个活跃设备。"
        right={data !== null && <span className="text-sm text-slate-400">共 {data.total}</span>}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-slate-500 mr-1">类型</span>
        {(['all', 'desktop', 'mobile'] as TypeFilter[]).map((t) => (
          <Chip key={t} active={typeFilter === t} onClick={(): void => { setTypeFilter(t); setOffset(0); }}>
            {t === 'all' ? '全部' : TYPE_LABEL[t]}
          </Chip>
        ))}
        <span className="text-xs text-slate-500 ml-3 mr-1">平台</span>
        {(['all', 'macos', 'windows', 'ios', 'android'] as PlatformFilter[]).map((p) => (
          <Chip key={p} active={platformFilter === p} onClick={(): void => { setPlatformFilter(p); setOffset(0); }}>
            {p === 'all' ? '全部' : PLATFORM_LABEL[p]}
          </Chip>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e): void => { setIncludeRevoked(e.target.checked); setOffset(0); }}
            className="accent-shark-500"
          />
          含已下线
        </label>
      </div>

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="mat-matte rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/[0.04]">
              <tr>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">用户</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">类型</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">平台</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">设备名</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">最近活跃</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">绑定</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">状态</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && data === null ? (
                <tr><td colSpan={8}><LoadingState /></td></tr>
              ) : data === null || data.devices.length === 0 ? (
                <tr><td colSpan={8}><EmptyState title="暂无设备" /></td></tr>
              ) : (
                data.devices.map((d) => {
                  const isRevoked = d.revoked_at !== null;
                  return (
                    <tr key={d.id} className="border-t border-white/[0.08]">
                      <td className="px-4 py-3">
                        <Link to={`/users/${d.user_id}`} className="text-shark-300 hover:text-shark-200">
                          {d.user_email ?? d.user_phone ?? d.user_id.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{TYPE_LABEL[d.device_type]}</td>
                      <td className="px-4 py-3 text-slate-300">{PLATFORM_LABEL[d.platform]}</td>
                      <td className="px-4 py-3 text-slate-400">{d.name ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-400">
                        {d.last_active_at !== null ? fmtDateTime(d.last_active_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{fmtDateTime(d.created_at)}</td>
                      <td className="px-4 py-3">
                        {isRevoked ? (
                          <span className="text-xs text-slate-500">
                            已下线 · {fmtDateTime(d.revoked_at)}
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-400">✓ 活跃</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isRevoked && (
                          <GlassButton
                            size="sm"
                            variant="danger"
                            disabled={busyId === d.id}
                            onClick={(): void => void revoke(d)}
                          >
                            强制下线
                          </GlassButton>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        offset={offset}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

